// TODO(api): Real backend integration. Currently scaffolded only.
// Maps the server error envelope { error: { code, message, fields, meta } } to
// ProDeploymentApiError. Activated by setting VITE_USE_MOCK_API=false; see
// pro-deployment.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  ProDeploymentApiError,
  type DeployHistoryEntry,
  type DeployInput,
  type DeployResult,
  type DiseaseFilterOption,
  type ItemPoolQuery,
  type Module,
  type ProDeploymentApi,
  type CurrentConfigQuery,
  type ProDeploymentApiErrorCode,
  type ProDeploymentCurrentConfig,
  type ProItemRef,
  type UpdateModuleInput,
  type UpdatePageConfigInput,
} from '../types/pro-deployment.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new ProDeploymentApiError(
    (payload?.code as ProDeploymentApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const proDeploymentHttpApi: ProDeploymentApi = {
  async getCurrentConfig(query?: CurrentConfigQuery): Promise<ProDeploymentCurrentConfig> {
    try {
      const res = await httpClient.get<ApiEnvelope<ProDeploymentCurrentConfig>>(
        endpoints.proDeployment,
        { params: { disease: query?.disease, weeks: query?.weeks } },
      );
      const data = res.data?.data;
      // The page gates on truthiness then iterates `modules`/`items`; a non-object
      // body or missing array fields must surface as a load error instead of
      // white-screening on `config.modules`/`config.items`.
      if (
        typeof data !== 'object' ||
        data === null ||
        !Array.isArray(data.modules) ||
        !Array.isArray(data.items)
      ) {
        throw new ProDeploymentApiError(
          'UNKNOWN',
          'PRO 배치 데이터를 불러올 수 없습니다.',
        );
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async listDiseaseOptions(): Promise<DiseaseFilterOption[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<DiseaseFilterOption[]>>(
        `${endpoints.proDeployment}/disease-options`,
      );
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new ProDeploymentApiError('UNKNOWN', '질환 필터 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async listItemPool(query: ItemPoolQuery): Promise<ProItemRef[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<ProItemRef[]>>(
        `${endpoints.proDeployment}/item-pool`,
        {
          params: {
            disease: query.disease,
            freq: query.freq,
            // SHCT-516 — module open weekday(s), comma-joined codes (e.g. 'THU',
            // 'MON,WED'; '' for daily/quarterly). BE matches items on the full
            // cadence (frequency + weekday), not frequency alone.
            weekdays: query.weekdays.join(','),
          },
        },
      );
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new ProDeploymentApiError('UNKNOWN', '항목 풀을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async updatePageConfig(input: UpdatePageConfigInput): Promise<{
    diseaseFilter: string;
    monitoringWeeks: number;
  }> {
    try {
      const res = await httpClient.patch<
        ApiEnvelope<{ diseaseFilter: string; monitoringWeeks: number }>
      >(endpoints.proDeployment, input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateModule(mid: string, input: UpdateModuleInput): Promise<Module> {
    try {
      const res = await httpClient.patch<ApiEnvelope<Module>>(
        `${endpoints.proDeployment}/modules/${mid}`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async deploy(input: DeployInput): Promise<DeployResult> {
    try {
      const res = await httpClient.post<ApiEnvelope<DeployResult>>(
        `${endpoints.proDeployment}/deploy`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async listHistory(limit?: number): Promise<DeployHistoryEntry[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<DeployHistoryEntry[]>>(
        `${endpoints.proDeployment}/history`,
        { params: { limit } },
      );
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new ProDeploymentApiError('UNKNOWN', '배포 이력을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async getResetCount(disease: string): Promise<number | null> {
    try {
      const res = await httpClient.get<ApiEnvelope<{ count: number | null }>>(
        `${endpoints.proDeployment}/reset-count`,
        { params: { disease } },
      );
      const count = res.data?.data?.count;
      if (count !== null && typeof count !== 'number') {
        throw new ProDeploymentApiError('UNKNOWN', '초기화 대상 문진 수를 불러올 수 없습니다.');
      }
      return count;
    } catch (e) {
      mapError(e);
    }
  },
};
